import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Receipt, Loader2, Coins, Zap, TrendingUp, Clock, Image, Video } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const WORKFLOW_LABELS: Record<string, string> = {
  "generate-fingerprint": "Persona Fingerprint",
  "generate-persona": "Persona Generation",
  "narrative-opportunity": "Narrative Opportunities",
  "narrative-angles": "Narrative Angles",
  "funnel-strategy": "Funnel Strategy",
  "ad-unit-plan": "Ad Unit Plan",
  "stage-ad-selection": "Stage Ad Selection",
  "assign-angles": "Angle Assignment",
  "campaign-plan": "Campaign Plan",
  "planner-persona": "Planner Persona",
  "creative-strategist": "Creative Strategist",
  "media-allocation": "Media Allocation",
  "creative-brief": "Creative Brief",
  "ideate-personas": "Persona Ideation",
  "test-sim-workflow": "Test Workflow",
  "nb-prompt": "Image Prompt",
  "ad-prompt": "Ad Prompt",
  "ugc-start-frame": "UGC Starting Frame",
  "ugc-script": "UGC Script",
  "ugc-scene-prompts": "UGC Scene Prompts",
  "scene-prompt": "Scene Prompt",
  "campaign-ad-image": "Campaign Ad Image",
  "avatar-gen": "Avatar Generation",
  "script-planner": "Script Planner",
  "script-planner-regen": "Script Regen",
  "script-gen": "Script Generation",
  "analyze-template": "Template Analysis",
  "ad-image-gen": "Ad Image Generation",
  "edit-image": "Image Edit",
  "ugc-start-frame-image": "UGC Start Frame",
  "video-gen-kling": "Video Generation (Kling)",
  "video-gen-seedance": "Video Generation (SeeDance)",
  "video-gen-minimax-h3": "Video Generation (MiniMax H3 Max 768p)",
  "campaign-ad-image-gen": "Campaign Ad Image",
  "avatar-image-gen": "Avatar Image Generation",
};

const WORKFLOW_CATEGORIES: Record<string, string> = {
  "generate-fingerprint": "Persona",
  "generate-persona": "Persona",
  "ideate-personas": "Persona",
  "planner-persona": "Persona",
  "narrative-opportunity": "Strategy",
  "narrative-angles": "Strategy",
  "funnel-strategy": "Strategy",
  "creative-strategist": "Strategy",
  "ad-unit-plan": "Campaign",
  "stage-ad-selection": "Campaign",
  "assign-angles": "Campaign",
  "campaign-plan": "Campaign",
  "media-allocation": "Campaign",
  "creative-brief": "Creative",
  "nb-prompt": "Creative",
  "ad-prompt": "Creative",
  "campaign-ad-image": "Creative",
  "scene-prompt": "Creative",
  "analyze-template": "Creative",
  "ugc-start-frame": "Video",
  "ugc-script": "Video",
  "ugc-scene-prompts": "Video",
  "script-planner": "Video",
  "script-planner-regen": "Video",
  "script-gen": "Video",
  "avatar-gen": "Media",
  "ad-image-gen": "Media",
  "edit-image": "Media",
  "ugc-start-frame-image": "Media",
  "video-gen-kling": "Video",
  "video-gen-seedance": "Video",
  "video-gen-minimax-h3": "Video",
  "campaign-ad-image-gen": "Media",
  "avatar-image-gen": "Media",
  "test-sim-workflow": "System",
};

function formatWorkflow(name: string): string {
  return WORKFLOW_LABELS[name] || name.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function getCategory(name: string): string {
  return WORKFLOW_CATEGORIES[name] || "Other";
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatFullDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type UsageLog = {
  id: string;
  workflowName: string;
  model: string | null;
  rawCostUsd: number;
  apiCostUsd: number;
  totalCostUsd: number;
  creditsCharged: number;
  executionId: string | null;
  metadata: { brandName?: string; entityName?: string; entityUrl?: string; unitType?: string; tier?: string; quantity?: number } | null;
  createdAt: string;
};

const UNIT_WORKFLOWS = new Set([
  "ad-image-gen", "edit-image", "ugc-start-frame-image",
  "video-gen-kling", "video-gen-seedance", "video-gen-minimax-h3",
  "campaign-ad-image-gen", "avatar-image-gen",
]);

const SUBSTEP_WORKFLOWS = new Set([
  "generate-fingerprint",
  "Persona Avatar",
  "Persona Fingerprint",
]);

type DisplayRow = {
  id: string;
  workflowName: string;
  model: string | null;
  rawCostUsd: number;
  apiCostUsd: number;
  totalCostUsd: number;
  creditsCharged: number;
  executionId: string | null;
  metadata: UsageLog["metadata"];
  createdAt: string;
  children?: UsageLog[];
};

function groupLogs(logs: UsageLog[]): DisplayRow[] {
  const sorted = [...logs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const result: DisplayRow[] = [];
  const consumed = new Set<string>();

  const byGroup = new Map<string, UsageLog[]>();
  for (const log of sorted) {
    const gid = (log.metadata as any)?.groupId;
    if (gid) {
      if (!byGroup.has(gid)) byGroup.set(gid, []);
      byGroup.get(gid)!.push(log);
    }
  }

  for (const [, group] of byGroup) {
    if (group.length <= 1) continue;
    const parent = group.find(g => !SUBSTEP_WORKFLOWS.has(g.workflowName)) || group[0];
    const children = group.filter(g => g.id !== parent.id);
    const totalCredits = group.reduce((s, e) => s + (e.creditsCharged || 0), 0);
    const totalCost = group.reduce((s, e) => s + (e.totalCostUsd || 0), 0);
    const totalRaw = group.reduce((s, e) => s + (e.rawCostUsd || 0), 0);
    const totalApi = group.reduce((s, e) => s + (e.apiCostUsd || 0), 0);
    group.forEach(g => consumed.add(g.id));
    result.push({
      ...parent,
      creditsCharged: totalCredits,
      totalCostUsd: totalCost,
      rawCostUsd: totalRaw,
      apiCostUsd: totalApi,
      children,
    });
  }

  for (const log of sorted) {
    if (consumed.has(log.id)) continue;
    if (SUBSTEP_WORKFLOWS.has(log.workflowName)) {
      const logTime = new Date(log.createdAt).getTime();
      const parent = result.find(r => {
        if (r.workflowName !== "generate-persona") return false;
        const diff = Math.abs(new Date(r.createdAt).getTime() - logTime);
        return diff < 300_000;
      });
      if (parent) {
        parent.creditsCharged += log.creditsCharged || 0;
        parent.totalCostUsd += log.totalCostUsd || 0;
        parent.rawCostUsd += log.rawCostUsd || 0;
        parent.apiCostUsd += log.apiCostUsd || 0;
        if (!parent.children) parent.children = [];
        parent.children.push(log);
        consumed.add(log.id);
        continue;
      }
    }
    result.push(log);
    consumed.add(log.id);
  }

  return result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export default function UsageLogsPage() {
  const [, setLocation] = useLocation();
  const { data: logs, isLoading } = useQuery<UsageLog[]>({
    queryKey: ["/api/me/usage-logs"],
    staleTime: 30_000,
    refetchOnMount: "always",
  });

  const { data: creditData } = useQuery<{ credits: number; balance: number; multiplier: number }>({
    queryKey: ["/api/me/credits"],
    refetchInterval: 10_000,
  });

  const grouped = logs ? groupLogs(logs) : [];
  const totalCredits = logs?.reduce((sum, l) => sum + (l.creditsCharged || 0), 0) ?? 0;
  const totalWorkflows = grouped.length;
  const credits = creditData?.credits ?? 0;
  const balance = creditData?.balance ?? 0;

  const todayLogs = grouped.filter(l => {
    const d = new Date(l.createdAt);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  });
  const todayCredits = todayLogs.reduce((sum, l) => sum + (l.creditsCharged || 0), 0);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">Usage Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Credits used on your account</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-primary/15 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Coins className="h-4 w-4 text-primary" />
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Remaining</span>
            </div>
            <p className="text-2xl font-bold tabular-nums" data-testid="text-credits-remaining">{credits.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">of {balance.toLocaleString()} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-orange-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Used</span>
            </div>
            <p className="text-2xl font-bold tabular-nums" data-testid="text-total-credits">{totalCredits.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{totalWorkflows} workflow{totalWorkflows !== 1 ? "s" : ""}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Zap className="h-4 w-4 text-green-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Today</span>
            </div>
            <p className="text-2xl font-bold tabular-nums" data-testid="text-today-credits">{todayCredits.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{todayLogs.length} workflow{todayLogs.length !== 1 ? "s" : ""}</p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <div className="h-12 w-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
              <Receipt className="h-6 w-6 opacity-50" />
            </div>
            <p className="text-sm font-medium">No usage yet</p>
            <p className="text-xs mt-1 opacity-70">Credits will appear here as you use AI workflows</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="divide-y">
              {grouped.map((log) => {
                const meta = log.metadata;
                const label = formatWorkflow(log.workflowName);
                const category = getCategory(log.workflowName);
                const isUnit = UNIT_WORKFLOWS.has(log.workflowName);
                const unitDetail = isUnit && meta
                  ? [
                      meta.unitType === "video" ? "Video" : "Image",
                      meta.tier ? `@ ${meta.tier}` : null,
                      meta.quantity && meta.quantity > 1 ? `× ${meta.quantity}` : null,
                    ].filter(Boolean).join(" ")
                  : null;
                const detail = [meta?.brandName, meta?.entityName].filter(Boolean).join(" / ");

                return (
                  <div
                    key={log.id}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors"
                    data-testid={`row-log-${log.id}`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
                      {isUnit && meta?.unitType === "video"
                        ? <Video className="h-4 w-4 text-muted-foreground" />
                        : isUnit
                        ? <Image className="h-4 w-4 text-muted-foreground" />
                        : <Zap className="h-4 w-4 text-muted-foreground" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{label}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal shrink-0">
                          {category}
                        </Badge>
                        {unitDetail && (
                          <span className="text-[10px] text-muted-foreground shrink-0">{unitDetail}</span>
                        )}
                      </div>
                      {log.model && isUnit && (
                        <div className="text-[11px] text-muted-foreground/70 mt-0.5 truncate">{log.model}</div>
                      )}
                      {detail && (
                        meta?.entityUrl ? (
                          <button
                            onClick={() => setLocation(meta.entityUrl!)}
                            className="text-xs text-primary hover:underline cursor-pointer mt-0.5 text-left truncate block max-w-full"
                            data-testid={`link-entity-${log.id}`}
                          >
                            {detail}
                          </button>
                        ) : (
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">{detail}</div>
                        )
                      )}
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums">{log.creditsCharged.toFixed(1)} cr</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5" title={formatFullDate(log.createdAt)}>
                        <Clock className="h-3 w-3 inline-block mr-0.5 -mt-px" />
                        {formatDate(log.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
